import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import { useDialogs } from '../context/DialogContext';
import adminApi from "../adminApi";
import "./DatabaseUpdate.css";

// ---- Types ----
interface CSVPreview {
  filePath: string;
  detectedSeparator: string;
  headers: string[];
  preview: string[][];
  totalRows: number;
}

// ---- Helper Components ----
const Spinner = () => <div className="spinner"></div>;
const UploadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;

function DatabaseUpdate() {
  const { t, tf, rows: rowCount } = useLanguage();
  const { confirm } = useDialogs();
  const [tables, setTables] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState<string>("");
  const [csvPreview, setCsvPreview] = useState<CSVPreview | null>(null);
  const [separator, setSeparator] = useState<string>(",");
  const [columnMapping, setColumnMapping] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [dbColumns, setDbColumns] = useState<string[]>([]);
  const [matchColumn, setMatchColumn] = useState<string>("");

  // Current table rows to compare with CSV preview
  const [currentRows, setCurrentRows] = useState<any[]>([]);
  const [compareLimit] = useState<number>(10); // default number of rows to show if CSV preview missing

  // show/hide the "new values" window below
  const [showNewWindow, setShowNewWindow] = useState<boolean>(true);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // A23: plik można upuścić na obszar pod ustawieniami (albo kliknąć go i wybrać)
  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  // Ostatnio wczytany plik - do ponownej analizy po zmianie separatora (K4)
  const lastFileRef = useRef<File | null>(null);

  // Lista tabel raz, przy wejściu do widoku
  useEffect(() => {
    fetchTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Plik upuszczony obok obszaru nie może otworzyć się w karcie zamiast panelu
  // (przeglądarka domyślnie przechodzi do upuszczonego pliku i panel znika)
  useEffect(() => {
    const preventFileDrop = (event: DragEvent) => {
      if (event.dataTransfer?.types.includes("Files")) event.preventDefault();
    };
    window.addEventListener("dragover", preventFileDrop);
    window.addEventListener("drop", preventFileDrop);
    return () => {
      window.removeEventListener("dragover", preventFileDrop);
      window.removeEventListener("drop", preventFileDrop);
    };
  }, []);

  useEffect(() => {
    if (selectedTable) {
      fetchTableColumns(selectedTable);
    }
  }, [selectedTable]);

  // Fetch current rows for comparison (limit equals CSV preview length or compareLimit)
  const fetchCurrentRows = async (limit = compareLimit) => {
    try {
      const resp = await adminApi.get(
        `/database/tables/${selectedTable}`,
        { params: { limit, offset: 0 } }
      );
      setCurrentRows(resp.data.data || []);
    } catch (err) {
      console.error("Error fetching current rows:", err);
      setCurrentRows([]);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await adminApi.get("/database/tables");
      setTables(response.data);
      if (response.data.length > 0) {
        setSelectedTable(response.data[0]);
      }
    } catch (err) {
      console.error('Error fetching tables:', err);
      setFeedback(t("Failed to load database tables"));
    }
  };

  const fetchTableColumns = async (tableName: string) => {
    try {
      const response = await adminApi.get(`/database/tables/${tableName}/columns`);
      setDbColumns(response.data);
    } catch (err) {
      console.error('Error fetching table columns:', err);
    }
  };

  // When a CSV preview is loaded, fetch current rows to compare
  useEffect(() => {
    if (csvPreview && selectedTable) {
      // fetch same number of rows as CSV preview (or fallback to compareLimit)
      const rowsToFetch = Math.max(csvPreview.preview.length, compareLimit);
      fetchCurrentRows(rowsToFetch);
    } else {
      setCurrentRows([]);
    }
    // fetchCurrentRows to zwykła funkcja (nowa przy każdym renderze), compareLimit się nie zmienia
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvPreview, selectedTable]);

  const uploadDisabled = isLoading || isImporting;

  const openFilePicker = () => {
    if (!uploadDisabled) fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // ten sam plik musi dać się wybrać drugi raz (np. po poprawieniu go w Excelu)
    e.target.value = '';
    if (file) await uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!uploadDisabled) setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // dragleave przychodzi też przy przejściu nad ikoną / tekstem w środku obszaru
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragActive(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (uploadDisabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) await uploadFile(file);
  };

  // separatorOverride: separator wybrany w polu "CSV-Trennzeichen" (ponowna analiza tego
  // samego pliku); bez niego backend rozpoznaje separator z pliku
  const uploadFile = async (file: File, separatorOverride?: string) => {
    if (!/\.csv$/i.test(file.name)) {
      setFeedback(t("Only .csv files can be imported."));
      return;
    }

    setIsLoading(true);
    setFeedback(`${t("Analyzing file")} "${file.name}"...`);

    try {
      const formData = new FormData();
      formData.append('csv', file);
      if (separatorOverride) formData.append('separator', separatorOverride);

      const response = await adminApi.post(
        "/csv/upload",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        setCsvPreview(response.data);
        setSeparator(response.data.detectedSeparator);

        // Auto-map columns if names match
        const autoMapping: { [key: string]: string } = {};
        response.data.headers.forEach((header: string, index: number) => {
          const matchingCol = dbColumns.find(col =>
            col.toLowerCase() === header.toLowerCase()
          );
          if (matchingCol) {
            autoMapping[index.toString()] = matchingCol;
          }
        });
        setColumnMapping(autoMapping);

        setFileName(file.name);
        lastFileRef.current = file;
        setFeedback(tf("File analyzed successfully. {rows} found.", { rows: rowCount(response.data.totalRows) }));
      }
    } catch (err) {
      console.error('Error uploading CSV:', err);
      setFeedback(t("Failed to upload CSV file"));
    } finally {
      setIsLoading(false);
    }
  };

  // The component will use the server-provided csvPreview.preview as-is.

  // K4: po zmianie separatora ten sam plik jest analizowany jeszcze raz - wcześniej trzeba
  // było wgrać go ponownie ręcznie, a podgląd i przypisanie kolumn zostawały ze starym podziałem
  const handleSeparatorChange = (newSeparator: string) => {
    setSeparator(newSeparator);
    if (lastFileRef.current && csvPreview) {
      uploadFile(lastFileRef.current, newSeparator);
    }
  };

  const handleColumnMap = (csvIndex: number, dbColumn: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [csvIndex.toString()]: dbColumn
    }));
  };

  const handleImport = async () => {
    if (!csvPreview || !selectedTable) {
      setFeedback(t("Please select a table and upload a CSV file first."));
      return;
    }

    const mappedColumns = Object.keys(columnMapping).length;
    if (mappedColumns === 0) {
      setFeedback(t("Please map at least one column before importing."));
      return;
    }

    const confirmed = await confirm({
      message: tf("Are you sure you want to import {rows} into table \"{table}\"? This cannot be undone.", { rows: rowCount(csvPreview.totalRows), table: selectedTable }),
      confirmLabel: t("Import"),
    });

    if (!confirmed) return;

    setIsImporting(true);
    setFeedback(t("Importing data..."));

    try {
      const response = await adminApi.post(
        "/csv/import",
        {
          filePath: csvPreview.filePath,
          tableName: selectedTable,
          separator: separator,
          columnMapping: columnMapping,
          matchColumn: matchColumn
        }
      );

      if (response.data.success) {
        setFeedback(tf("{rows} imported successfully!", { rows: rowCount(response.data.importedCount) }));
        setCsvPreview(null);
        setFileName(null);
        lastFileRef.current = null;
        setColumnMapping({});
        setMatchColumn("");
        setTimeout(() => setFeedback(null), 5000);
      }
    } catch (err: any) {
      console.error('Error importing CSV:', err);
      setFeedback(err.response?.data?.error || t("Failed to import CSV data"));
    } finally {
      setIsImporting(false);
    }
  };

  // columns sequence used by both current-data and new-values tables
  const columnsSeq: string[] = csvPreview
    ? (Object.values(columnMapping).length > 0
      ? Array.from(new Set(Object.values(columnMapping)))
      : (dbColumns.length ? dbColumns : csvPreview.headers))
    : [];

  return (
    <div className="db-update-container">
      <div className="db-update-header">
        <div>
          <h2 className="db-update-title">{t("Database Import from CSV")}</h2>
          <p className="db-update-subtitle">{t("Import and update database tables from CSV files with column mapping.")}</p>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".csv"
          style={{ display: 'none' }}
        />
      </div>

      {feedback && <div className="feedback-bar">{feedback}</div>}

      <div className="import-config">
        <div className="config-row">
          <div className="form-group">
            <label>{t("Target Table")}</label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              disabled={isImporting}
            >
              {tables.map(table => (
                <option key={table} value={table}>{table}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t("CSV Separator")}</label>
            <select
              value={separator}
              onChange={(e) => handleSeparatorChange(e.target.value)}
              disabled={isImporting}
            >
              <option value=",">{t("Comma (,)")}</option>
              <option value=";">{t("Semicolon (;)")}</option>
              {/* {"\t"}, bo value="\t" w JSX to dosłownie backslash i "t", a nie tabulator */}
              <option value={"\t"}>{t("Tab")}</option>
              <option value="|">{t("Pipe (|)")}</option>
            </select>
          </div>

          <div className="form-group">
            <label>{t("Upsert Match Column (Optional)")}</label>
            <select
              value={matchColumn}
              onChange={(e) => setMatchColumn(e.target.value)}
              disabled={isImporting}
              title={t("If selected, existing records with matching value in this column will be updated instead of inserted.")}
            >
              <option value="">{t("-- No Match (Always Insert) --")}</option>
              {dbColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
          </div>
        </div>

        {/* A23: obszar upuszczania zamiast przycisku "CSV hochladen" w nagłówku. Pod wyborem
            tabeli, bo kolumny CSV są dopasowywane do kolumn wybranej tabeli. */}
        <div
          className={`csv-dropzone${dragActive ? " is-dragging" : ""}${csvPreview ? " is-compact" : ""}${uploadDisabled ? " is-disabled" : ""}`}
          role="button"
          tabIndex={uploadDisabled ? -1 : 0}
          aria-disabled={uploadDisabled}
          aria-label={t("Upload CSV")}
          onClick={openFilePicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openFilePicker();
            }
          }}
          onDragEnter={handleDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <span className="csv-dropzone__icon">{isLoading ? <Spinner /> : <UploadIcon />}</span>
          <span className="csv-dropzone__text">
            <strong>
              {isLoading
                ? t("Processing...")
                : dragActive
                  ? t("Release to upload the file")
                  : csvPreview
                    ? t("Drop another CSV file here or click to replace it")
                    : t("Drop a CSV file here or click to choose one")}
            </strong>
            {csvPreview && fileName ? (
              <span className="csv-dropzone__hint">{t("File")}: {fileName}</span>
            ) : (
              <span className="csv-dropzone__hint">{t("Choose the target table first - CSV columns are matched to its columns.")}</span>
            )}
          </span>
        </div>
      </div>

      {csvPreview && (
        <>
          <div className="column-mapping">
            <h3>{t("Column Mapping")}</h3>
            <p className="mapping-subtitle">{t("Map CSV columns to database columns")}</p>
            <div className="mapping-grid">
              {csvPreview.headers.map((header, index) => (
                <div key={index} className="mapping-row">
                  <div className="csv-column">
                    <strong>{header}</strong>
                    <span className="column-preview">
                      {csvPreview.preview[0]?.[index] || 'N/A'}
                    </span>
                  </div>
                  <div className="mapping-arrow">→</div>
                  <select
                    className="db-column-select"
                    value={columnMapping[index.toString()] || ''}
                    onChange={(e) => handleColumnMap(index, e.target.value)}
                  >
                    <option value="">{t("-- Skip Column --")}</option>
                    {dbColumns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="compare-inline" style={{ marginTop: 12 }}>
            <h3 style={{ marginBottom: 8 }}>{t("Current Data (top). New values appear in the panel below.")}</h3>

            {/* Current Data table styled like New Values (smaller font, nowrap, diff highlight) */}
            <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 4 }}>
              <table className="preview-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, whiteSpace: "nowrap" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>#</th>
                    {columnsSeq.map((col) => (
                      <th key={col} style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.preview.map((row, rowIndex) => (
                    <tr key={"dbrow_" + rowIndex}>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{rowIndex + 1}</td>
                      {columnsSeq.map((mappedCol, i) => {
                        const csvIndex = Object.entries(columnMapping).find(([, v]) => v === mappedCol)?.[0];
                        const csvVal = csvIndex !== undefined ? String(row[Number(csvIndex)] ?? "") : (row[i] ?? "");
                        const dbVal = currentRows[rowIndex] ? String(currentRows[rowIndex][mappedCol] ?? "") : "";
                        const different = csvVal !== dbVal;
                        const style: React.CSSProperties = different
                          ? { background: "#fff4f4", padding: "6px 8px", borderBottom: "1px solid #eee" }
                          : { background: "#f6fff6", padding: "6px 8px", borderBottom: "1px solid #eee" };
                        return (
                          <td key={"dbcell_" + mappedCol + "_" + i} style={style}>
                            {dbVal || "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 8 }}>
              <button
                className="btn"
                onClick={() => setShowNewWindow(v => !v)}
                style={{ marginBottom: 8 }}
              >
                {showNewWindow ? t("Hide new values panel") : t("Show new values panel")}
              </button>
            </div>

            {showNewWindow && (
              <div
                className="new-values-window"
                style={{
                  marginTop: 8,
                  border: "1px solid #cfcfcf",
                  borderRadius: 6,
                  padding: 8,
                  background: "#fafafa",
                  maxHeight: 360,
                  overflow: "auto",
                }}
              >
                <div style={{ fontSize: 12, marginBottom: 6, fontWeight: 600 }}>{t("New Values (CSV) — differences highlighted")}</div>
                <div style={{ overflowX: "auto" }}>
                  <table className="preview-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, whiteSpace: "nowrap" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>#</th>
                        {columnsSeq.map((mappedCol) => (
                          <th key={mappedCol} style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>{mappedCol}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.preview.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{rowIndex + 1}</td>
                          {columnsSeq.map((mappedCol, i) => {
                            const csvIndex = Object.entries(columnMapping).find(([, v]) => v === mappedCol)?.[0];
                            const csvVal = csvIndex !== undefined ? String(row[Number(csvIndex)] ?? "") : (row[i] ?? "");
                            const dbVal = currentRows[rowIndex] ? String(currentRows[rowIndex][mappedCol] ?? "") : "";
                            const different = csvVal !== dbVal;
                            const style: React.CSSProperties = different
                              ? { background: "#fff4f4", padding: "6px 8px", borderBottom: "1px solid #eee" }
                              : { background: "#f6fff6", padding: "6px 8px", borderBottom: "1px solid #eee" };
                            return (
                              <td key={mappedCol + "_" + i} style={style}>{csvVal || "—"}</td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="import-footer" style={{ marginTop: 12 }}>
            <button
              className="btn btn-success"
              onClick={handleImport}
              disabled={isImporting || Object.keys(columnMapping).length === 0}
            >
              {isImporting ? (
                <><Spinner /> {t("Importing...")}</>
              ) : (
                tf("Import {rows}", { rows: rowCount(csvPreview.totalRows) })
              )}
            </button>
          </div>
        </>
      )}

    </div>
  );
}

export default DatabaseUpdate;